/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {derivePathStack, isSyntheticHistoryState} from '@app/utils/HistoryBootstrap';

interface NavigationLike {
	canGoBack: boolean;
	back(): unknown;
	addEventListener(type: string, listener: (event: Event) => void): void;
	removeEventListener(type: string, listener: (event: Event) => void): void;
}

const getNavigation = (): NavigationLike | null => {
	if (typeof window === 'undefined') return null;
	const nav = (window as unknown as {navigation?: NavigationLike}).navigation;
	return nav ?? null;
};

/**
 * Whether the browser has a real previous entry to traverse to. Uses the
 * Navigation API (Baseline as of Safari 26.2) when available; otherwise
 * checks history.length and the synthetic-state flag.
 */
export function canGoBack(): boolean {
	const nav = getNavigation();
	if (nav) return nav.canGoBack;
	if (typeof window === 'undefined') return false;
	return window.history.length > 1;
}

/**
 * Subscribe to changes in canGoBack. Returns an unsubscribe function.
 */
export function onCanGoBackChange(listener: () => void): () => void {
	const nav = getNavigation();
	if (nav) {
		const handler = () => listener();
		nav.addEventListener('currententrychange', handler);
		return () => nav.removeEventListener('currententrychange', handler);
	}
	if (typeof window === 'undefined') return () => {};
	const handler = () => listener();
	window.addEventListener('popstate', handler);
	return () => window.removeEventListener('popstate', handler);
}

/**
 * Navigate back. If the browser stack genuinely has no previous entry (cold
 * deep-link with no synthetic stack synthesized), synthesize one synchronously
 * and then navigate to the fallback. This keeps back-button UI from being a
 * no-op.
 */
export function goBackOr(fallbackPath: string): void {
	if (typeof window === 'undefined') return;
	if (canGoBack()) {
		window.history.back();
		return;
	}
	const parents = derivePathStack(window.location.pathname);
	const target = fallbackPath || parents[parents.length - 1] || '/';
	const currentPath = window.location.pathname + window.location.search + window.location.hash;
	if (target === currentPath) return;
	window.history.replaceState(null, '', target);
	window.history.pushState(null, '', currentPath);
	window.history.back();
}

/**
 * Read whether the current entry was synthesized at boot. Useful for UI that
 * wants to know if "back" will land on a real prior page or a reconstructed one.
 */
export function isCurrentEntrySynthetic(): boolean {
	if (typeof window === 'undefined') return false;
	return isSyntheticHistoryState(window.history.state);
}
